import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Fade,
  Slide,
  Alert,
  Paper,
  useMediaQuery,
} from "@mui/material";
import { 
  Visibility, 
  VisibilityOff, 
  Person, 
  Lock, 
  Login as LoginIcon 
} from "@mui/icons-material";
import { createTheme, ThemeProvider, keyframes } from "@mui/material/styles";
import { login as loginService } from "../services/authService";
import { AuthContext } from "../context/AuthContext";
import { Link, NavLink, useNavigate } from "react-router-dom";
import comhubLogo from "../assets/background.png";

// Animaciones personalizadas
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(50, 105, 144, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(50, 105, 144, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(50, 105, 144, 0);
  }
`;

const theme = createTheme({
  palette: {
    primary: {
      main: "#0B2039",
      light: "#326990",
      dark: "#051119",
    },
    secondary: {
      main: "#326990",
      light: "#5A8BB0",
      dark: "#1E4A66",
    },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
    text: {
      primary: "#1a202c",
      secondary: "#4a5568",
    },
    error: {
      main: "#e53e3e",
      light: "#fc8181",
    },
    success: {
      main: "#38a169",
      light: "#68d391",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h6: {
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.95rem",
      lineHeight: 1.6,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            },
            '&.Mui-focused': {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 20px rgba(50, 105, 144, 0.2)',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 12,
          padding: '12px 24px',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
          },
        },
      },
    },
  },
});

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ username: "", password: "" });
  const [isVisible, setIsVisible] = useState(false);
  const { login: loginContext } = useContext(AuthContext);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Validación en tiempo real
  const validateField = (field, value) => {
    const errors = { ...fieldErrors };
    
    switch (field) {
      case 'username':
        if (!value.trim()) {
          errors.username = "El nombre de usuario es requerido";
        } else if (value.length < 3) {
          errors.username = "El nombre debe tener al menos 3 caracteres";
        } else {
          errors.username = "";
        }
        break;
      case 'password':
        if (!value) {
          errors.password = "La contraseña es requerida";
        } else if (value.length < 6) {
          errors.password = "La contraseña debe tener al menos 6 caracteres";
        } else {
          errors.password = "";
        }
        break;
      default:
        break;
    }
    
    setFieldErrors(errors);
    return errors[field] === "";
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    validateField('username', value);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    validateField('password', value);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    // Validar todos los campos antes de enviar
    const isUsernameValid = validateField('username', username);
    const isPasswordValid = validateField('password', password);

    if (!isUsernameValid || !isPasswordValid) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await loginService(username, password);

      await loginContext(response);
      navigate("/movilidadElectrica/reservas");
    } catch (authError) {
      if (authError.message && authError.message.includes("401")) {
        setError("Usuario o contraseña incorrectos.");
      } else {
        setError("Ocurrió un error. Por favor, inténtalo de nuevo.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleShowPassword = () => setShowPassword((prev) => !prev);

  const isFormValid = username.trim() && password && !fieldErrors.username && !fieldErrors.password;

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          width: "100vw",
          background: isMobile 
            ? 'linear-gradient(135deg, #0B2039 0%, #326990 100%)'
            : `linear-gradient(135deg, rgba(11, 32, 57, 0.9) 0%, rgba(50, 105, 144, 0.8) 100%), url(${comhubLogo})`,
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Elementos decorativos */}
        <Box
          sx={{
            position: "absolute",
            top: "-50%",
            right: "-20%",
            width: "600px",
            height: "600px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.05)",
            animation: `${pulse} 4s ease-in-out infinite`,
            display: { xs: 'none', md: 'block' },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-30%",
            left: "-10%",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.03)",
            animation: `${pulse} 6s ease-in-out infinite`,
            display: { xs: 'none', md: 'block' },
          }}
        />

        {/* Contenedor principal */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: { xs: "1rem", sm: "2rem" },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Fade in={isVisible} timeout={800}>
            <Paper
              elevation={24}
              sx={{
                width: "100%",
                maxWidth: { xs: "400px", sm: "450px" },
                background: "rgba(255, 255, 255, 0.98)",
                backdropFilter: "blur(20px)",
                borderRadius: "24px",
                padding: { xs: "2rem", sm: "3rem" },
                textAlign: "center",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                animation: `${fadeIn} 0.8s ease-out`,
                position: "relative",
                overflow: "hidden",
                '&::before': {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: "linear-gradient(90deg, #0B2039 0%, #326990 100%)",
                },
              }}
            >
              {/* Header del formulario */}
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    width: "80px",
                    height: "80px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #0B2039 0%, #326990 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 1.5rem",
                    animation: `${slideIn} 0.6s ease-out 0.2s both`,
                  }}
                >
                  <LoginIcon sx={{ color: "white", fontSize: "2rem" }} />
                </Box>
                
                <Typography
                  variant="h4"
                  component="h1"
                  color="text.primary"
                  gutterBottom
                  sx={{
                    animation: `${slideIn} 0.6s ease-out 0.3s both`,
                  }}
                >
                  ¡Bienvenido de vuelta!
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    animation: `${slideIn} 0.6s ease-out 0.4s both`,
                  }}
                >
                  Ingresa tus credenciales para continuar
                </Typography>
              </Box>

              {/* Formulario */}
              <Box
                component="form"
                onSubmit={handleLogin}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  animation: `${slideIn} 0.6s ease-out 0.5s both`,
                }}
                noValidate
                autoComplete="off"
              >
                <TextField
                  fullWidth
                  label="Nombre de Usuario"
                  value={username}
                  onChange={handleUsernameChange}
                  error={!!fieldErrors.username}
                  helperText={fieldErrors.username}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color={fieldErrors.username ? "error" : "primary"} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    },
                  }}
                />

                <TextField
                  fullWidth
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  error={!!fieldErrors.password}
                  helperText={fieldErrors.password}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color={fieldErrors.password ? "error" : "primary"} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton 
                          onClick={toggleShowPassword} 
                          edge="end"
                          sx={{
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    },
                  }}
                />

                {/* Mostrar errores */}
                <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
                  <Alert 
                    severity="error" 
                    sx={{ 
                      borderRadius: '12px',
                      animation: `${slideIn} 0.3s ease-out`,
                    }}
                  >
                    {error}
                  </Alert>
                </Slide>

                {/* Botón de login */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  type="submit"
                  disabled={isLoading || !isFormValid}
                  sx={{
                    background: isFormValid 
                      ? "linear-gradient(135deg, #0B2039 0%, #326990 100%)"
                      : "rgba(0,0,0,0.12)",
                    color: "white",
                    fontWeight: 700,
                    fontSize: "1.1rem",
                    padding: "16px",
                    borderRadius: "12px",
                    position: "relative",
                    overflow: "hidden",
                    '&:hover': {
                      background: isFormValid 
                        ? "linear-gradient(135deg, #051119 0%, #1E4A66 100%)"
                        : "rgba(0,0,0,0.12)",
                    },
                    '&:disabled': {
                      color: "rgba(0,0,0,0.26)",
                    },
                    '&::before': {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                      transition: "left 0.6s ease-in-out",
                    },
                    '&:hover::before': {
                      left: "100%",
                    },
                  }}
                >
                  {isLoading ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} color="inherit" />
                      <span>Iniciando sesión...</span>
                    </Box>
                  ) : (
                    "Iniciar Sesión"
                  )}
                </Button>

                {/* Link de registro */}
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    ¿No tienes una cuenta?{" "}
                    <a
                      href="https://docs.google.com/forms/d/e/1FAIpQLSd6tCh29eUHPiWjN3rnLn4L0wAmeQFJhxAosmAffmIV1AUfcg/viewform?usp=header"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#326990",
                        fontWeight: 600,
                        textDecoration: "none",
                        transition: "all 0.2s ease-in-out",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = "#0B2039";
                        e.target.style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = "#326990";
                        e.target.style.textDecoration = "none";
                      }}
                    >
                      Regístrate aquí
                    </a>
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Login;
